/**
 * RSA-OAEP-SHA256 encryption of the login payload.
 * Fetches the gateway's public key once per page-load and encrypts the
 * { email, password, tenantCode? } object before sending /auth/login.
 */

let _pubKey: CryptoKey | null = null;
let _fetching: Promise<CryptoKey> | null = null;

function pemToArrayBuffer(pem: string): ArrayBuffer {
  const b64 = pem
    .replace(/-----BEGIN [^-]+-----/, '')
    .replace(/-----END [^-]+-----/, '')
    .replace(/\s+/g, '');
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

function bufToB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

async function getKey(authUrl: string): Promise<CryptoKey> {
  if (_pubKey) return _pubKey;
  if (_fetching) return _fetching;
  _fetching = (async () => {
    const res = await fetch(`${authUrl}/auth/login-key`);
    if (!res.ok) throw new Error('login_key_fetch_failed');
    const { publicKey } = (await res.json()) as { publicKey: string };
    const key = await crypto.subtle.importKey(
      'spki',
      pemToArrayBuffer(publicKey),
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt'],
    );
    _pubKey = key;
    return key;
  })();
  try { return await _fetching; }
  finally { _fetching = null; }
}

export async function encryptLoginPayload(
  authUrl: string,
  payload: { email: string; password: string; tenantCode?: string },
): Promise<string> {
  const key = await getKey(authUrl);
  const data = new TextEncoder().encode(JSON.stringify(payload));
  const ct = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, data);
  return bufToB64(ct);
}